import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Book, 
  Code, 
  Shield, 
  Zap, 
  Users, 
  Settings, 
  FileText,
  Search,
  ChevronRight,
  ExternalLink,
  Copy,
  Check,
  AlertTriangle,
  Info,
  Terminal,
  Database,
  Key,
  BarChart,
  Filter,
  Bell,
  Download,
  Upload,
  Globe,
  Lock,
  CreditCard,
  HelpCircle,
  PlayCircle,
  BookOpen,
  Layers,
  Package,
  GitBranch,
  Cloud,
  Server,
  Cpu,
  Activity
} from 'lucide-react'

export default function Documentation() {
  const [searchQuery, setSearchQuery] = useState('')
  const [copiedCode, setCopiedCode] = useState<string | null>(null)
  const [selectedSection, setSelectedSection] = useState('getting-started')

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedCode(id)
    setTimeout(() => setCopiedCode(null), 2000)
  }

  const CodeBlock = ({ code, language = 'javascript', id }: { code: string; language?: string; id: string }) => (
    <div className="relative group">
      <pre className="bg-muted/50 border rounded-lg p-4 overflow-x-auto">
        <code className={`language-${language} text-sm`}>{code}</code>
      </pre>
      <Button
        size="sm"
        variant="ghost"
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={() => copyToClipboard(code, id)}
      >
        {copiedCode === id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  )

  const sections = {
    'getting-started': {
      title: 'Getting Started',
      icon: PlayCircle,
      content: (
        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-bold mb-4">Welcome to Form Entry Fraud Detection</h2>
            <p className="text-muted-foreground mb-6">
              Our platform provides real-time fraud detection for form submissions, protecting your business 
              from fake leads, bot attacks, and fraudulent entries with 95%+ accuracy.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Quick Start Guide
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <h3 className="font-semibold">1. Create Your Account</h3>
                <p className="text-sm text-muted-foreground">
                  Sign up for a free trial to get your API keys and access the dashboard.
                </p>
                <Button asChild variant="outline" size="sm">
                  <Link to="/auth/request-access">Request Account →</Link>
                </Button>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold">2. Install the SDK</h3>
                <p className="text-sm text-muted-foreground">
                  Choose your preferred integration method:
                </p>
                <Tabs defaultValue="javascript" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="javascript">JavaScript</TabsTrigger>
                    <TabsTrigger value="python">Python</TabsTrigger>
                    <TabsTrigger value="curl">cURL</TabsTrigger>
                  </TabsList>
                  <TabsContent value="javascript">
                    <CodeBlock 
                      id="install-js"
                      language="bash"
                      code="npm install @frauddetection/sdk"
                    />
                  </TabsContent>
                  <TabsContent value="python">
                    <CodeBlock 
                      id="install-py"
                      language="bash"
                      code="pip install frauddetection-sdk"
                    />
                  </TabsContent>
                  <TabsContent value="curl">
                    <CodeBlock 
                      id="install-curl"
                      language="bash"
                      code="# No installation needed for cURL\n# Use our REST API directly"
                    />
                  </TabsContent>
                </Tabs>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold">3. Initialize the Client</h3>
                <CodeBlock 
                  id="init-client"
                  language="javascript"
                  code={`import { FraudDetection } from '@frauddetection/sdk';

const detector = new FraudDetection({
  apiKey: 'YOUR_API_KEY',
  environment: 'production'
});`}
                />
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold">4. Check a Lead</h3>
                <CodeBlock 
                  id="check-lead"
                  language="javascript"
                  code={`const result = await detector.checkLead({
  email: 'user@example.com',
  phone: '555-0123',
  ip: '192.168.1.1',
  name: 'John Doe',
  zip: '90210'
});

console.log('Risk Score:', result.riskScore);
console.log('Fraud Detected:', result.isFraud);
console.log('Patterns:', result.patterns);`}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                Platform Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Real-time Detection
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Sub-100ms response times with 95%+ accuracy using machine learning and pattern analysis.
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Pattern Database
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Continuously updated fraud patterns including sequential phones, datacenter IPs, and velocity attacks.
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <BarChart className="h-4 w-4" />
                    Financial Impact
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Track ROI with detailed reports showing exactly how much fraud prevention saves your business.
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Team Management
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Role-based access control with audit logs and collaborative fraud review workflows.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )
    },
    'api-reference': {
      title: 'API Reference',
      icon: Code,
      content: (
        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-bold mb-4">API Reference</h2>
            <p className="text-muted-foreground mb-6">
              Complete reference for our REST API and WebSocket real-time endpoints.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Authentication</CardTitle>
              <CardDescription>All API requests require authentication via API key</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <CodeBlock 
                id="auth-header"
                language="http"
                code={`Authorization: Bearer YOUR_API_KEY
Content-Type: application/json`}
              />
              
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-900 dark:text-amber-100">Security Notice</p>
                    <p className="text-amber-700 dark:text-amber-300">
                      Never expose your API key in client-side code. Use server-side proxy endpoints.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Core Endpoints</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Check Lead Endpoint */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-2 py-1 rounded text-xs font-medium">
                    POST
                  </span>
                  <code className="text-sm font-mono">/api/v1/leads/check</code>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  Analyze a lead for fraud patterns and return risk assessment.
                </p>

                <div className="space-y-2">
                  <p className="text-sm font-medium">Request Body:</p>
                  <CodeBlock 
                    id="check-lead-body"
                    language="json"
                    code={`{
  "email": "user@example.com",
  "phone": "555-0123",
  "name": "John Doe",
  "ip": "192.168.1.1",
  "zip": "90210",
  "metadata": {
    "source": "contact-form",
    "campaign": "summer-2024"
  }
}`}
                  />
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium">Response:</p>
                  <CodeBlock 
                    id="check-lead-response"
                    language="json"
                    code={`{
  "leadId": "lead_abc123",
  "riskScore": 78,
  "risk": "high",
  "isFraud": true,
  "patterns": [
    {
      "type": "sequential_phone",
      "confidence": 0.95,
      "details": "Phone in sequence 555-0120 to 555-0125"
    },
    {
      "type": "datacenter_ip",
      "confidence": 0.99,
      "details": "IP from DigitalOcean datacenter"
    }
  ],
  "recommendation": "block",
  "financialImpact": {
    "savedAmount": 30,
    "currency": "USD"
  }
}`}
                  />
                </div>
              </div>

              {/* Bulk Check Endpoint */}
              <div className="space-y-3 pt-6 border-t">
                <div className="flex items-center gap-2">
                  <span className="bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 px-2 py-1 rounded text-xs font-medium">
                    POST
                  </span>
                  <code className="text-sm font-mono">/api/v1/leads/bulk-check</code>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  Process multiple leads simultaneously (up to 1000 per request).
                </p>

                <div className="space-y-2">
                  <p className="text-sm font-medium">Request Body:</p>
                  <CodeBlock 
                    id="bulk-check-body"
                    language="json"
                    code={`{
  "leads": [
    {
      "email": "user1@example.com",
      "phone": "555-0123",
      "ip": "192.168.1.1"
    },
    {
      "email": "user2@example.com",
      "phone": "555-0124",
      "ip": "192.168.1.2"
    }
  ],
  "options": {
    "includePatterns": true,
    "calculateImpact": true
  }
}`}
                  />
                </div>
              </div>

              {/* Get Lead Details */}
              <div className="space-y-3 pt-6 border-t">
                <div className="flex items-center gap-2">
                  <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-2 py-1 rounded text-xs font-medium">
                    GET
                  </span>
                  <code className="text-sm font-mono">/api/v1/leads/{`{leadId}`}</code>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  Retrieve detailed information about a previously checked lead.
                </p>
              </div>

              {/* Update Lead Status */}
              <div className="space-y-3 pt-6 border-t">
                <div className="flex items-center gap-2">
                  <span className="bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 px-2 py-1 rounded text-xs font-medium">
                    PATCH
                  </span>
                  <code className="text-sm font-mono">/api/v1/leads/{`{leadId}`}/status</code>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  Manually override the status of a lead (approve, reject, review).
                </p>

                <div className="space-y-2">
                  <p className="text-sm font-medium">Request Body:</p>
                  <CodeBlock 
                    id="update-status-body"
                    language="json"
                    code={`{
  "status": "approved",
  "reason": "Manual review - legitimate customer",
  "reviewedBy": "user@company.com"
}`}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>WebSocket API</CardTitle>
              <CardDescription>Real-time updates for fraud detection events</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <p className="text-sm font-medium">Connection:</p>
                <CodeBlock 
                  id="websocket-connect"
                  language="javascript"
                  code={`const ws = new WebSocket('wss://api.frauddetection.com/ws');

ws.onopen = () => {
  // Authenticate
  ws.send(JSON.stringify({
    type: 'auth',
    apiKey: 'YOUR_API_KEY'
  }));
  
  // Subscribe to events
  ws.send(JSON.stringify({
    type: 'subscribe',
    events: ['fraud_detected', 'pattern_alert', 'velocity_attack']
  }));
};

ws.onmessage = (event) => {
  const data = JSON.parse(event.data);
  console.log('Event:', data.type, data.payload);
};`}
                />
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Event Types:</p>
                <div className="space-y-2">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <code className="text-sm font-mono">fraud_detected</code>
                    <p className="text-xs text-muted-foreground mt-1">
                      Triggered when a fraudulent lead is detected
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <code className="text-sm font-mono">pattern_alert</code>
                    <p className="text-xs text-muted-foreground mt-1">
                      New fraud pattern identified in your traffic
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <code className="text-sm font-mono">velocity_attack</code>
                    <p className="text-xs text-muted-foreground mt-1">
                      Rapid submission rate detected from single source
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Rate Limits</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <h4 className="font-medium mb-2">Free Tier</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• 100 checks per minute</li>
                      <li>• 10,000 checks per day</li>
                      <li>• 1 WebSocket connection</li>
                    </ul>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <h4 className="font-medium mb-2">Pro Tier</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• 1,000 checks per minute</li>
                      <li>• Unlimited daily checks</li>
                      <li>• 10 WebSocket connections</li>
                    </ul>
                  </div>
                </div>
                
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm">
                    Rate limit headers are included in all responses:
                  </p>
                  <code className="text-xs font-mono block mt-2">
                    X-RateLimit-Limit: 100<br />
                    X-RateLimit-Remaining: 95<br />
                    X-RateLimit-Reset: 1640995200
                  </code>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )
    },
    'patterns': {
      title: 'Fraud Patterns',
      icon: Shield,
      content: (
        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-bold mb-4">Fraud Pattern Detection</h2>
            <p className="text-muted-foreground mb-6">
              Understanding the fraud patterns our system detects and how to interpret them.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Pattern Types</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Sequential Phone Pattern */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="bg-red-100 dark:bg-red-900/30 p-2 rounded-lg">
                    <Terminal className="h-5 w-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Sequential Phone Numbers</h3>
                    <p className="text-sm text-muted-foreground">
                      Detects phone numbers in sequence, indicating automated form filling
                    </p>
                  </div>
                </div>
                
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium">Detection Logic:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• Numbers within ±5 of each other</li>
                    <li>• Same area code and prefix</li>
                    <li>• Submitted within 30 minutes</li>
                    <li>• Confidence increases with sequence length</li>
                  </ul>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium">Example Pattern:</p>
                  <CodeBlock 
                    id="sequential-example"
                    language="text"
                    code={`555-0120 → 555-0121 → 555-0122 → 555-0123
Time gaps: 45s, 1m 12s, 38s
Confidence: 98.5%`}
                  />
                </div>
              </div>

              {/* Data Center IP Pattern */}
              <div className="space-y-3 pt-6 border-t">
                <div className="flex items-center gap-3">
                  <div className="bg-orange-100 dark:bg-orange-900/30 p-2 rounded-lg">
                    <Server className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Data Center IPs</h3>
                    <p className="text-sm text-muted-foreground">
                      Traffic from hosting providers and cloud services
                    </p>
                  </div>
                </div>
                
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium">Common Sources:</p>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="text-sm">
                      <span className="font-medium">• AWS</span>
                      <span className="text-muted-foreground ml-2">82% fraud rate</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">• DigitalOcean</span>
                      <span className="text-muted-foreground ml-2">85% fraud rate</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">• Hetzner</span>
                      <span className="text-muted-foreground ml-2">61% fraud rate</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">• OVH</span>
                      <span className="text-muted-foreground ml-2">58% fraud rate</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Velocity Attack Pattern */}
              <div className="space-y-3 pt-6 border-t">
                <div className="flex items-center gap-3">
                  <div className="bg-yellow-100 dark:bg-yellow-900/30 p-2 rounded-lg">
                    <Zap className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Velocity Attacks</h3>
                    <p className="text-sm text-muted-foreground">
                      Rapid form submissions from single source
                    </p>
                  </div>
                </div>
                
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium">Thresholds:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• 3+ submissions in 60 seconds</li>
                    <li>• 10+ submissions in 5 minutes</li>
                    <li>• 50+ submissions in 1 hour</li>
                  </ul>
                </div>
              </div>

              {/* Email Pattern */}
              <div className="space-y-3 pt-6 border-t">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Suspicious Email Domains</h3>
                    <p className="text-sm text-muted-foreground">
                      Temporary and disposable email services
                    </p>
                  </div>
                </div>
                
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium">Common Patterns:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• 10minutemail.com, tempmail.com</li>
                    <li>• guerrillamail.com, mailinator.com</li>
                    <li>• Random character strings before @</li>
                    <li>• Newly registered domains (&lt;30 days)</li>
                  </ul>
                </div>
              </div>

              {/* Geographic Concentration */}
              <div className="space-y-3 pt-6 border-t">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                    <Globe className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Geographic Concentration</h3>
                    <p className="text-sm text-muted-foreground">
                      Unusual concentration of leads from specific locations
                    </p>
                  </div>
                </div>
                
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium">Alert Triggers:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• 30%+ of leads from single zip code</li>
                    <li>• 50%+ increase in specific location</li>
                    <li>• Mismatched IP and zip code locations</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Pattern Correlations</CardTitle>
              <CardDescription>How patterns work together to indicate fraud</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex -space-x-2">
                      <div className="bg-red-100 dark:bg-red-900/50 p-1 rounded-full border-2 border-white dark:border-gray-900">
                        <Terminal className="h-3 w-3 text-red-600" />
                      </div>
                      <div className="bg-orange-100 dark:bg-orange-900/50 p-1 rounded-full border-2 border-white dark:border-gray-900">
                        <Globe className="h-3 w-3 text-orange-600" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium">Sequential + Geographic</h4>
                      <p className="text-sm text-muted-foreground">92% correlation strength</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Sequential phone numbers from concentrated zip codes indicate organized fraud rings.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex -space-x-2">
                      <div className="bg-orange-100 dark:bg-orange-900/50 p-1 rounded-full border-2 border-white dark:border-gray-900">
                        <Server className="h-3 w-3 text-orange-600" />
                      </div>
                      <div className="bg-yellow-100 dark:bg-yellow-900/50 p-1 rounded-full border-2 border-white dark:border-gray-900">
                        <Zap className="h-3 w-3 text-yellow-600" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium">Data Center + Velocity</h4>
                      <p className="text-sm text-muted-foreground">87% correlation strength</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Rapid submissions from cloud IPs indicate bot attacks.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )
    },
    'integration': {
      title: 'Integration Guides',
      icon: Package,
      content: (
        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-bold mb-4">Integration Guides</h2>
            <p className="text-muted-foreground mb-6">
              Step-by-step guides for integrating fraud detection into your applications.
            </p>
          </div>

          <Tabs defaultValue="wordpress" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="wordpress">WordPress</TabsTrigger>
              <TabsTrigger value="shopify">Shopify</TabsTrigger>
              <TabsTrigger value="react">React</TabsTrigger>
              <TabsTrigger value="custom">Custom</TabsTrigger>
            </TabsList>

            <TabsContent value="wordpress" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>WordPress Integration</CardTitle>
                  <CardDescription>Protect Contact Form 7, Gravity Forms, and WPForms</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <h3 className="font-semibold">1. Install Plugin</h3>
                    <p className="text-sm text-muted-foreground">
                      Download and install our WordPress plugin from the repository.
                    </p>
                    <CodeBlock 
                      id="wp-install"
                      language="bash"
                      code="wp plugin install fraud-detection --activate"
                    />
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-semibold">2. Configure API Key</h3>
                    <p className="text-sm text-muted-foreground">
                      Go to Settings → Fraud Detection and enter your API key.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-semibold">3. Hook Implementation</h3>
                    <CodeBlock 
                      id="wp-hook"
                      language="php"
                      code={`// Automatically added by plugin
add_filter('wpcf7_validate', function($result, $tags) {
    $fraud_check = check_fraud_detection([
        'email' => $_POST['your-email'],
        'phone' => $_POST['your-phone'],
        'ip' => $_SERVER['REMOTE_ADDR']
    ]);
    
    if ($fraud_check->risk_score > 70) {
        $result->invalidate($tags[0], 'Submission blocked for security reasons.');
    }
    
    return $result;
}, 10, 2);`}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="shopify" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Shopify Integration</CardTitle>
                  <CardDescription>Protect customer registrations and checkout forms</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <h3 className="font-semibold">1. Install App</h3>
                    <p className="text-sm text-muted-foreground">
                      Install from Shopify App Store and authorize permissions.
                    </p>
                    <Button variant="outline" size="sm">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View in App Store
                    </Button>
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-semibold">2. Webhook Configuration</h3>
                    <CodeBlock 
                      id="shopify-webhook"
                      language="javascript"
                      code={`// Automatic webhook registration
{
  "webhook": {
    "topic": "customers/create",
    "address": "https://api.frauddetection.com/shopify/webhook",
    "format": "json"
  }
}`}
                    />
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-semibold">3. Checkout Script</h3>
                    <CodeBlock 
                      id="shopify-script"
                      language="liquid"
                      code={`{% comment %} Add to checkout.liquid {% endcomment %}
<script>
  window.fraudDetection = {
    storeId: '{{ shop.id }}',
    sessionId: '{{ checkout.token }}'
  };
</script>
<script src="https://cdn.frauddetection.com/shopify.js"></script>`}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="react" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>React Integration</CardTitle>
                  <CardDescription>Add fraud detection to your React forms</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <h3 className="font-semibold">1. Install Package</h3>
                    <CodeBlock 
                      id="react-install"
                      language="bash"
                      code="npm install @frauddetection/react"
                    />
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-semibold">2. Create Hook</h3>
                    <CodeBlock 
                      id="react-hook"
                      language="javascript"
                      code={`import { useFraudDetection } from '@frauddetection/react';

function ContactForm() {
  const { checkLead, isChecking } = useFraudDetection({
    apiKey: process.env.REACT_APP_FRAUD_API_KEY
  });

  const handleSubmit = async (formData) => {
    const result = await checkLead({
      email: formData.email,
      phone: formData.phone,
      name: formData.name
    });

    if (result.isFraud) {
      alert('This submission has been blocked.');
      return;
    }

    // Continue with form submission
    submitForm(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Form fields */}
    </form>
  );
}`}
                    />
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-semibold">3. Protected Form Component</h3>
                    <CodeBlock 
                      id="react-component"
                      language="javascript"
                      code={`import { FraudProtectedForm } from '@frauddetection/react';

function App() {
  return (
    <FraudProtectedForm
      onSubmit={handleValidSubmission}
      onFraudDetected={handleFraudDetection}
      threshold={70}
    >
      <input name="email" type="email" required />
      <input name="phone" type="tel" required />
      <button type="submit">Submit</button>
    </FraudProtectedForm>
  );
}`}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="custom" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Custom Integration</CardTitle>
                  <CardDescription>Build your own integration with any platform</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <h3 className="font-semibold">Server-Side Validation</h3>
                    <Tabs defaultValue="node" className="w-full">
                      <TabsList>
                        <TabsTrigger value="node">Node.js</TabsTrigger>
                        <TabsTrigger value="python">Python</TabsTrigger>
                        <TabsTrigger value="php">PHP</TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="node">
                        <CodeBlock 
                          id="custom-node"
                          language="javascript"
                          code={`const axios = require('axios');

async function validateLead(leadData) {
  try {
    const response = await axios.post(
      'https://api.frauddetection.com/v1/leads/check',
      leadData,
      {
        headers: {
          'Authorization': 'Bearer YOUR_API_KEY',
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.data.riskScore > 70) {
      throw new Error('High fraud risk detected');
    }

    return response.data;
  } catch (error) {
    console.error('Fraud check failed:', error);
    throw error;
  }
}`}
                        />
                      </TabsContent>

                      <TabsContent value="python">
                        <CodeBlock 
                          id="custom-python"
                          language="python"
                          code={`import requests

def validate_lead(lead_data):
    response = requests.post(
        'https://api.frauddetection.com/v1/leads/check',
        json=lead_data,
        headers={
            'Authorization': 'Bearer YOUR_API_KEY',
            'Content-Type': 'application/json'
        }
    )
    
    result = response.json()
    
    if result['riskScore'] > 70:
        raise ValueError('High fraud risk detected')
    
    return result`}
                        />
                      </TabsContent>

                      <TabsContent value="php">
                        <CodeBlock 
                          id="custom-php"
                          language="php"
                          code={`function validateLead($leadData) {
    $ch = curl_init('https://api.frauddetection.com/v1/leads/check');
    
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($leadData));
    curl_setopt($ch, CURLOPT_HTTPHEADER, [
        'Authorization: Bearer YOUR_API_KEY',
        'Content-Type: application/json'
    ]);
    
    $response = curl_exec($ch);
    $result = json_decode($response, true);
    
    if ($result['riskScore'] > 70) {
        throw new Exception('High fraud risk detected');
    }
    
    return $result;
}`}
                        />
                      </TabsContent>
                    </Tabs>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )
    },
    'dashboard-guide': {
      title: 'Dashboard Guide',
      icon: BarChart,
      content: (
        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-bold mb-4">Dashboard User Guide</h2>
            <p className="text-muted-foreground mb-6">
              Learn how to use the fraud detection dashboard to monitor and manage your leads.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Dashboard Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <h4 className="font-semibold">Real-Time Metrics</h4>
                  <p className="text-sm text-muted-foreground">
                    Monitor leads processed, fraud detected, and money saved in real-time.
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold">Recent Leads Table</h4>
                  <p className="text-sm text-muted-foreground">
                    View and manage recent submissions with risk scores and patterns.
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold">Active Patterns</h4>
                  <p className="text-sm text-muted-foreground">
                    See currently active fraud patterns affecting your traffic.
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold">Quick Actions</h4>
                  <p className="text-sm text-muted-foreground">
                    Export data, configure auto-blocking, and adjust detection settings.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Lead Management</CardTitle>
              <CardDescription>Review and manage individual leads</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <h3 className="font-semibold">Risk Score Interpretation</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-3 py-1 rounded-full text-sm font-medium">
                      0-30
                    </div>
                    <div>
                      <p className="font-medium">Low Risk</p>
                      <p className="text-sm text-muted-foreground">Likely legitimate, auto-approve recommended</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-3 py-1 rounded-full text-sm font-medium">
                      31-70
                    </div>
                    <div>
                      <p className="font-medium">Medium Risk</p>
                      <p className="text-sm text-muted-foreground">Manual review recommended</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-3 py-1 rounded-full text-sm font-medium">
                      71-100
                    </div>
                    <div>
                      <p className="font-medium">High Risk</p>
                      <p className="text-sm text-muted-foreground">Likely fraudulent, auto-block recommended</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold">Export Formats</h3>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-4 w-4" />
                      <span className="font-medium">CSV Export</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Spreadsheet-compatible format with all lead data
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Download className="h-4 w-4" />
                      <span className="font-medium">JSON Export</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Complete data with patterns and metadata
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )
    },
    'security': {
      title: 'Security & Compliance',
      icon: Lock,
      content: (
        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-bold mb-4">Security & Compliance</h2>
            <p className="text-muted-foreground mb-6">
              Our commitment to data security and regulatory compliance.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Data Security</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Encryption
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    AES-256 encryption at rest, TLS 1.3 in transit
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Key className="h-4 w-4" />
                    API Key Security
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Scoped permissions, automatic rotation, audit logging
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Infrastructure
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    SOC 2 Type II certified data centers, 99.99% uptime SLA
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Monitoring
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    24/7 security monitoring, intrusion detection, DDoS protection
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Compliance</CardTitle>
              <CardDescription>Meeting global data protection standards</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">GDPR Compliant</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• Right to erasure (data deletion on request)</li>
                    <li>• Data portability (export all your data)</li>
                    <li>• Privacy by design architecture</li>
                    <li>• EU data residency options</li>
                  </ul>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">CCPA Compliant</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• California consumer privacy rights</li>
                    <li>• Opt-out mechanisms</li>
                    <li>• Data sale prohibition</li>
                    <li>• Transparent privacy policy</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )
    },
    'faq': {
      title: 'FAQ',
      icon: HelpCircle,
      content: (
        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-bold mb-4">Frequently Asked Questions</h2>
            <p className="text-muted-foreground mb-6">
              Common questions about our fraud detection platform.
            </p>
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How accurate is the fraud detection?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Our system achieves 95%+ accuracy with less than 2% false positive rate. The machine learning 
                  models are continuously updated based on new fraud patterns and feedback from manual reviews.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What happens to blocked leads?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Blocked leads are quarantined for 30 days for review. You can manually approve any false 
                  positives, and the system learns from these corrections. After 30 days, blocked lead data 
                  is anonymized and used to improve pattern detection.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Can I customize the risk thresholds?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Yes! You can set custom thresholds for auto-approval, manual review, and auto-blocking. 
                  Different thresholds can be configured for different forms or traffic sources. 
                  Navigate to Settings → Detection Rules to customize.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">How fast is the API response?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Average response time is under 100ms for single lead checks. Bulk checks process at 
                  approximately 10,000 leads per second. Our infrastructure scales automatically to 
                  handle traffic spikes without performance degradation.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What&apos;s the difference between Free and Pro tiers?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Key differences include:
                  </p>
                  <div className="grid gap-3 md:grid-cols-2 text-sm">
                    <div className="bg-muted/50 rounded-lg p-3">
                      <h4 className="font-medium mb-2">Free Tier</h4>
                      <ul className="text-muted-foreground space-y-1 text-xs">
                        <li>• 10,000 checks/month</li>
                        <li>• Basic patterns</li>
                        <li>• Email support</li>
                        <li>• 30-day data retention</li>
                      </ul>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3">
                      <h4 className="font-medium mb-2">Pro Tier</h4>
                      <ul className="text-muted-foreground space-y-1 text-xs">
                        <li>• Unlimited checks</li>
                        <li>• Advanced ML patterns</li>
                        <li>• Priority support</li>
                        <li>• 1-year data retention</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Can I integrate with my CRM?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Yes! We offer native integrations with Salesforce, HubSpot, Pipedrive, and Zoho CRM. 
                  For other systems, you can use our webhook API or Zapier integration to sync lead 
                  status and fraud scores automatically.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    }
  }

  const navigation = [
    { id: 'getting-started', icon: PlayCircle, label: 'Getting Started' },
    { id: 'api-reference', icon: Code, label: 'API Reference' },
    { id: 'patterns', icon: Shield, label: 'Fraud Patterns' },
    { id: 'integration', icon: Package, label: 'Integration Guides' },
    { id: 'dashboard-guide', icon: BarChart, label: 'Dashboard Guide' },
    { id: 'security', icon: Lock, label: 'Security & Compliance' },
    { id: 'faq', icon: HelpCircle, label: 'FAQ' }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-muted-foreground hover:text-foreground">
                <ChevronRight className="h-5 w-5 rotate-180" />
              </Link>
              <div className="flex items-center gap-2">
                <Book className="h-6 w-6 text-primary" />
                <h1 className="text-3xl font-bold">Documentation</h1>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search docs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Button asChild variant="outline">
                <a href="https://github.com/frauddetection/docs" target="_blank" rel="noopener noreferrer">
                  <GitBranch className="h-4 w-4 mr-2" />
                  GitHub
                </a>
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="text-sm">Navigation</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[calc(100vh-200px)]">
                  <div className="p-4 space-y-1">
                    {navigation.map((item) => {
                      const Icon = item.icon
                      return (
                        <Button
                          key={item.id}
                          variant={selectedSection === item.id ? 'secondary' : 'ghost'}
                          className="w-full justify-start"
                          onClick={() => setSelectedSection(item.id)}
                        >
                          <Icon className="h-4 w-4 mr-2" />
                          {item.label}
                        </Button>
                      )
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            <ScrollArea className="h-[calc(100vh-120px)]">
              {sections[selectedSection]?.content}
            </ScrollArea>
          </div>
        </div>
      </div>
    </div>
  )
}